<template>
  <div class="camera-capture-root">
    <div v-if="!cameraIsReady" class="camera-loader">
      <div class="spinner"></div>
      <p class="loader-text">Iniciando cámara...</p>
    </div>

    <header v-if="currentFacingMode === 'user'" class="facial_access__header">
      <div class="logo-container-white">
        <img v-if="logo" :src="logo" class="facial_access__header--logo" />
        <fuseaccess-logo v-else class="facial_access__header--logo"></fuseaccess-logo>
      </div>
    </header>

    <div 
      v-if="currentFacingMode === 'user'" 
      class="instruction-bar" 
      :class="{ ready: faceReady }"
    >
      {{ faceReady ? "Tomando foto…" : hintText }}
    </div>

    <div 
      class="camera-modal-overlay" 
      :class="{ 
        'full-screen-mode': currentFacingMode === 'environment',
        'is-hidden': !cameraIsReady 
      }"
    >
      <div class="camera-modal">
        <video 
          ref="video" 
          autoplay 
          playsinline 
          @playing="onVideoPlaying"
          :style="currentFacingMode === 'environment' ? 'transform: none' : 'transform: scaleX(-1)'"
        ></video>

        <div 
          v-if="currentFacingMode === 'user'" 
          class="face-indicator" 
          :class="{ ready: faceReady }"
        ></div>

        <div v-if="currentFacingMode === 'environment'" class="manual-capture-container">
          <button @click="capturePhoto" class="btn-capture-circle"></button>
          <p class="btn-label">Tomar foto</p>
        </div>

        <div v-if="countdown > 0" class="countdown">
          {{ countdown }}
        </div>

        <canvas ref="canvas" style="display:none"></canvas>
      </div>
    </div>
  </div>
</template>

<script>
import * as faceapi from "face-api.js";
import FuseaccessLogo from '../shared/icons/FuseaccessLogo.vue';

export default {
  name: "CameraCapture",
  components: {
    FuseaccessLogo
  },
  props: {
    currentFacingMode: {
      type: String,
      default: "user"
    },
    logo: {
      type: String,
      default: null
    }
  },

  data() {
    return {
      cameraIsReady: false,
      hasError: false, // Nueva validación de estado
      countdown: 0,
      countdownTimer: null,
      detectInterval: null,
      faceReady: false,
      hintText: "Detectando rostro…",
      eyeOpenFrames: 0,
      REQUIRED_EYE_FRAMES: 5,
      isCountingDown: false,
      initTimeout: null // Timeout para detectar si la cámara nunca abre
    };
  },

  async mounted() {
    console.log("Cámara solicitada con facingMode:", this.currentFacingMode);
    if (this.currentFacingMode === 'user') {
      await this.loadModels();
    }
    await this.initializeCamera();
  },

  beforeDestroy() {
    this.stopCamera();
    clearTimeout(this.initTimeout);
    clearInterval(this.detectInterval);
    clearInterval(this.countdownTimer);
  },

  methods: {
    onVideoPlaying() {
      clearTimeout(this.initTimeout);
      this.cameraIsReady = true;
      this.hasError = false;
    },

    async loadModels() {
      try {
        await faceapi.nets.tinyFaceDetector.loadFromUri("/models");
        await faceapi.nets.faceLandmark68TinyNet.loadFromUri("/models");
      } catch (e) {
        console.error("Error al cargar modelos de IA:", e);
      }
    },

    async initializeCamera() {
      this.cameraIsReady = false;
      this.hasError = false;

      // Si en 7 segundos no hay imagen (caso Iriun desconectado o error), emitimos cierre
      this.initTimeout = setTimeout(() => {
        if (!this.cameraIsReady) {
          console.error("Tiempo de espera de cámara agotado.");
          this.$emit("close", "timeout_error");
        }
      }, 7000);

      if (this.$refs.video && this.$refs.video.srcObject) {
        this.$refs.video.srcObject.getTracks().forEach(t => t.stop());
      }

      setTimeout(async () => {
        try {
          const isBack = this.currentFacingMode === 'environment';
          
          // 1. Validación de hardware: ¿Existen cámaras?
          const devices = await navigator.mediaDevices.enumerateDevices();
          const videoDevices = devices.filter(d => d.kind === 'videoinput');

          if (videoDevices.length === 0) {
            throw new Error("No se detectaron cámaras en este dispositivo.");
          }

          let videoConstraints = {
            facingMode: isBack ? "environment" : "user"
          };

          // 2. Lógica para forzar cámara trasera si hay múltiples (evita Iriun si es necesario)
          if (isBack && videoDevices.length > 1) {
            // Buscamos un ID que no sea el primero para intentar saltar drivers virtuales
            const backCamera = videoDevices.find(d => 
              d.label.toLowerCase().includes('back') || 
              d.label.toLowerCase().includes('trasera')
            );
            if (backCamera) {
              videoConstraints = { deviceId: { exact: backCamera.deviceId } };
            }
          }

          const stream = await navigator.mediaDevices.getUserMedia({
            video: videoConstraints
          });

          const video = this.$refs.video;
          if (!video) return;

          video.srcObject = stream;

          video.onloadedmetadata = async () => {
            await video.play();
            this.startDetection();
          };

        } catch (err) {
          console.warn("Fallo en inicialización de cámara:", err);
          this.handleCameraError();
        }
      }, 300);
    },

    handleCameraError() {
      // Intento final con constraints básicas si falla lo anterior
      navigator.mediaDevices.getUserMedia({ video: true })
        .then(stream => {
          if (this.$refs.video) {
            this.$refs.video.srcObject = stream;
            this.$refs.video.onloadedmetadata = () => {
              this.$refs.video.play();
              this.startDetection();
            };
          }
        })
        .catch(() => {
          this.hasError = true;
          this.$emit("close", "not_allowed");
        });
    },

    startDetection() {
      if (this.currentFacingMode === 'environment') {
        this.faceReady = true;
        return;
      }

      const video = this.$refs.video;
      const options = new faceapi.TinyFaceDetectorOptions({
        inputSize: 224,
        scoreThreshold: 0.6
      });

      this.detectInterval = setInterval(async () => {
        if (!video || video.paused || video.ended) return;

        const detection = await faceapi
          .detectSingleFace(video, options)
          .withFaceLandmarks(true);

        if (this.isCountingDown) {
          if (!detection || !this.isFaceCenteredAndStraight(detection, video) || !this.eyesOpen(detection)) {
            this.cancelCountdown("Debe permanecer fijo y mirando a la cámara");
          }
          return;
        }

        if (!detection) {
          this.resetState("Coloque su rostro frente a la cámara");
          return;
        }

        if (!this.isFaceCenteredAndStraight(detection, video)) {
          this.resetState("Por favor, centre su rostro y mire al frente");
          return;
        }

        if (!this.eyesOpen(detection)) {
          this.resetState("Abra los ojos");
          return;
        }

        this.eyeOpenFrames++;
        if (this.eyeOpenFrames < this.REQUIRED_EYE_FRAMES) {
          this.faceReady = false;
          this.hintText = "Mantenga los ojos abiertos";
          return;
        }

        this.faceReady = true;
        this.hintText = "No se mueva";
        this.startCountdown();
      }, 350);
    },

    resetState(message) {
      this.faceReady = false;
      this.hintText = message;
      this.eyeOpenFrames = 0;
    },

    startCountdown() {
      if (this.isCountingDown) return;
      this.isCountingDown = true;
      this.countdown = 3;
      this.countdownTimer = setInterval(() => {
        this.countdown--;
        if (this.countdown === 0) {
          clearInterval(this.countdownTimer);
          this.capturePhoto();
        }
      }, 1000);
    },

    cancelCountdown(message) {
      clearInterval(this.countdownTimer);
      this.isCountingDown = false;
      this.countdown = 0;
      this.faceReady = false;
      this.eyeOpenFrames = 0;
      this.hintText = message;
    },

    capturePhoto() {
      const video = this.$refs.video;
      const canvas = this.$refs.canvas;
      const ctx = canvas.getContext("2d");

      canvas.width = 900;
      canvas.height = 1200;

      const targetRatio = canvas.width / canvas.height;
      const videoRatio = video.videoWidth / video.videoHeight;

      let sx, sy, sw, sh;
      if (videoRatio > targetRatio) {
        sh = video.videoHeight;
        sw = sh * targetRatio;
        sx = (video.videoWidth - sw) / 2;
        sy = 0;
      } else {
        sw = video.videoWidth;
        sh = sw / targetRatio;
        sx = 0;
        sy = (video.videoHeight - sh) / 2;
      }

      ctx.drawImage(video, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);

      canvas.toBlob(blob => {
        if (!blob) return;
        const file = new File([blob], "capture.jpg", { type: "image/jpeg" });
        this.$emit("captured", { target: { files: [file] } });
        this.stopCamera();
      }, "image/jpeg", 0.85);
    },

    stopCamera() {
      const stream = this.$refs.video?.srcObject;
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
      if (this.$refs.video) this.$refs.video.srcObject = null;
    },

    isFaceCenteredAndStraight(detection, video) {
      const box = detection.detection.box;
      const landmarks = detection.landmarks;
      const faceCenterX = box.x + box.width / 2;
      const videoCenterX = video.videoWidth / 2;
      if (Math.abs(faceCenterX - videoCenterX) > video.videoWidth * 0.15) return false;
      if (box.width < video.videoWidth * 0.20) return false;
      const leftEye = landmarks.getLeftEye();
      const rightEye = landmarks.getRightEye();
      const nose = landmarks.getNose();
      const eyesCenterX = ((leftEye[0].x + leftEye[3].x) / 2 + (rightEye[0].x + rightEye[3].x) / 2) / 2;
      return Math.abs(nose[3].x - eyesCenterX) < box.width * 0.10;
    },

    eyesOpen(detection) {
      const d = (p1, p2) => Math.hypot(p1.x - p2.x, p1.y - p2.y);
      const ear = (eye) => ((d(eye[1], eye[5]) + d(eye[2], eye[4])) / (2 * d(eye[0], eye[3])));
      return (ear(detection.landmarks.getLeftEye()) > 0.24 && ear(detection.landmarks.getRightEye()) > 0.24);
    }
  }
};
</script>

<style scoped>
.camera-capture-root {
  position: fixed;
  inset: 0;
  z-index: 2000;
  background-color: #ededed;
}

.camera-loader {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: #ededed;
  z-index: 3000;
}

.spinner {
  width: 50px;
  height: 50px;
  border: 5px solid rgba(46, 163, 198, 0.2);
  border-top-color: #2ea3c6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 15px;
}

.loader-text {
  color: #2ea3c6;
  font-weight: 600;
  font-family: sans-serif;
}

@keyframes spin { to { transform: rotate(360deg); } }

.facial_access__header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 90px;
  background-color: #ededed;
  z-index: 1100;
  display: flex;
  align-items: center;
  justify-content: center;
}

.logo-container-white {
  height: 100%;
  padding: 0 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0 0 8px 8px;
}

.instruction-bar {
  position: fixed;
  top: 90px;
  left: 0;
  right: 0;
  min-height: 70px;
  padding: 0 16px; 
  background: #2ea3c6;
  color: white;
  font-weight: 700;
  font-size: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  z-index: 1090;
  transition: background-color 0.3s ease;
}

.instruction-bar.ready { background: #00b26b; }

.camera-modal-overlay {
  position: fixed;
  top: 160px;
  left: 0;
  right: 0;
  bottom: 0;
  background: black;
  transition: opacity 0.3s ease;
}

.camera-modal-overlay.is-hidden {
  opacity: 0;
}

.camera-modal-overlay.full-screen-mode {
  top: 0 !important;
}

.camera-modal { width: 100%; height: 100%; position: relative; }

video {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.face-indicator {
  position: absolute;
  width: 280px;
  height: 280px;
  border-radius: 50%;
  border: 5px solid red;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  pointer-events: none;
}

.face-indicator.ready { border-color: #00ff88; }

.manual-capture-container {
  position: absolute;
  bottom: 40px;
  left: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

.btn-capture-circle {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  border: 6px solid white;
  cursor: pointer;
}

.btn-capture-circle:active { transform: scale(0.9); }

.btn-label { color: white; font-weight: bold; text-shadow: 0 2px 4px rgba(0,0,0,0.5); }

.countdown {
  position: absolute;
  inset: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 160px;
  color: white;
  background: rgba(0,0,0,.3);
}

.facial_access__header--logo {
  max-height: stretch;
  width: auto;
}
</style>